// Role hierarchy utilities for the Platinum application

import { RolePermissions } from '../types/app'

/**
 * Role-based Admin Access Control:
 * - Manager role: Full admin access with all features
 * - Realm role: Limited admin view (simplified user management)
 * - Other roles: No admin access at all
 */

/**
 * Check if a user has Realm role or below (i.e., only has Realm role and no higher roles)
 * @param roles Array of role strings from the user's session
 * @returns true if the user only has Realm role or no roles at all
 */
export function isRealmLevelOrBelow(roles: string[]): boolean {
  // Higher level roles that indicate the user has elevated permissions
  const higherRoles = ['Manager', 'Tables', 'Admin', 'TextAnalysis', 'Analyst', 'Silver']
  
  // Check if user has any higher level role
  const hasHigherRole = roles.some(role => higherRoles.includes(role))
  
  // User is Realm level or below if they don't have any higher roles
  return !hasHigherRole
}

/**
 * Check if a user has admin privileges (Manager or Admin role)
 * @param roles Array of role strings from the user's session
 * @returns true if the user has Manager or Admin role
 */
export function isAdminUser(roles: string[]): boolean {
  return roles.includes('Manager') || roles.includes('Admin')
}

/**
 * Capability gate for promoting/demoting a dashboard to company-level, and
 * for overwriting a company-level dashboard you don't own (#962): Manager or
 * Realm. Deliberately NOT `isAdminUser()` above — that checks for an 'Admin'
 * role string that doesn't exist in this app and would wrongly exclude Realm,
 * locking customer admins out of their own dashboards.
 *
 * `role_permissions.isRealmAdmin` is already computed as `isManager ||
 * isRealmAdmin` (see contexts/account.tsx), so it already means exactly
 * "Manager or Realm" despite the name — this just gives that capability a
 * name that says what it is for at each call site.
 */
export function canPromoteDashboard(rolePermissions: RolePermissions | undefined): boolean {
  return Boolean(rolePermissions?.isRealmAdmin)
}

/**
 * Get the highest role level for a user
 * @param roles Array of role strings from the user's session
 * @returns The highest role name or 'None' if no roles
 */
export function getHighestRole(roles: string[]): string {
  const roleHierarchy = ['Manager', 'Admin', 'Silver', 'Analyst', 'TextAnalysis', 'Tables', 'Realm']
  
  for (const role of roleHierarchy) {
    if (roles.includes(role)) {
      return role
    }
  }
  
  return 'None'
}
